import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, firstValueFrom, of } from 'rxjs';
import { SessionStorageService } from './storage/session-storage.service';
import { environment } from '@env/environment';
import { initSentryFromDsn } from '../sentry/sentry-init';

export interface KeyValueModel {
    key: string;
    value: string;
}

export interface LanguageResource {
    key: string;
    iso1: string;
    url: string;
}

interface ConfigApiResponse {
    isSuccess: boolean;
    statusCode: number;
    result?: string;
    errors?: any[];
}

enum InitializationState {
    IDLE = 'idle',
    LOADING = 'loading',
    SUCCESS = 'success',
    FAILED = 'failed',
}

interface RetryConfig {
    maxRetries: number;
    baseDelayMs: number;
    maxDelayMs: number;
}

interface AppConfiguration {
    agreements: {
        tr: { accessKey: string; sales: string; terms: string };
        en: { accessKey: string; sales: string; terms: string };
    };
    google: { web: { clientId: string } };
    microsoft: { web: { clientId: string; tenantId: string } };
    apple: { web: { ssoIdentifierId: string } };
    header: { xAppWeb: string };
    localization: LanguageResource[];
    mixpanelProjectToken: string;
    googleAnalytics: { measurementId: string };
    facebook: { appId: string; web: { clientToken: string } };
}

class AppConfigKeys {
    welcomeVideoUrlWebKey = 'App.Web.Welcome.Video.URL';
    agreementSalesEnKey = 'Core.Asset.Agreements';
    googleWebClientIdKey = 'Core.Google.Web.ClientId';
    facebookAppIdKey = 'Core.Facebook.AppId';
    facebookWebAppIdKey = 'Core.Facebook.ClientToken';
    microsoftWebClientIdKey = 'Core.Microsoft.ApplicationId';
    microsoftWebTenantIdKey = 'Core.Microsoft.DirectoryId';
    appleWebSSOIdentifierKey = 'Core.Apple.Web.SSOId';
    appHeaderXAppWebKey = 'App.Header.XApp.Web';
    localizationKey = 'App.Cropmap.Language.File.URL';
    mixpanelProjectTokenKey = 'App.Cropmap.Mixpanel.ProjectToken';
    googleAnalyticsMeasurementIdKey = 'Core.GoogleAnalytics.Web.MeasurementId';
}

class AuthenticatedConfigKeys {
    mapboxApiTokenKey = 'Core.Mapbox.Api.Token.Public';
    mapboxStyleKey = 'Core.Mapbox.Style.Cropmap';
    mapboxApiUrlKey = 'Core.Mapbox.Api.URL';
    mapboxApiTokenClientSecretKey = 'Core.Mapbox.Api.Token.Client.Secret';
    sentryDsnWebKey = 'App.Cropmap.Sentry.DSN';
}

@Injectable({
    providedIn: 'root',
})
export class AppConfigService {
    private _appConfigKeys = new AppConfigKeys();
    private _authenticatedConfigKeys = new AuthenticatedConfigKeys();
    private _appConfigurations: Record<string, string> | undefined;
    private _appConfigurationObject: AppConfiguration | undefined;
    private _authenticatedConfigurations: Record<string, string> | undefined;

    private _initState: InitializationState = InitializationState.IDLE;
    private _authInitState: InitializationState = InitializationState.IDLE;

    private _initPromise: Promise<boolean> | null = null;
    private _authInitPromise: Promise<boolean> | null = null;

    private readonly _retryConfig: RetryConfig = {
        maxRetries: 3,
        baseDelayMs: 1000,
        maxDelayMs: 10000,
    };

    private readonly SESSION_STORAGE_KEY = 'app_configurations';
    private readonly SESSION_STORAGE_OBJECT_KEY = 'app_configuration_object';
    private readonly AUTHENTICATED_SESSION_STORAGE_KEY = 'authenticated_configurations';

    private readonly _http = inject(HttpClient);
    private readonly _sessionStorageService = inject(SessionStorageService);
    private readonly _apiUrl = `${environment.apiBaseUrl}/v4.0/config`;

    async initializeApp(): Promise<boolean> {
        if (this._initState === InitializationState.SUCCESS) {
            return true;
        }

        if (this._initState === InitializationState.FAILED) {
            console.warn('AppConfig: Previous initialization failed. Use forceRefreshConfigurations() to retry.');
            return false;
        }

        if (this._initState === InitializationState.LOADING && this._initPromise) {
            return this._initPromise;
        }

        const cachedConfig = this.loadFromSessionStorage();
        const cachedObject = this.loadObjectFromSessionStorage();
        if (cachedConfig && cachedObject) {
            this._appConfigurations = cachedConfig;
            this._appConfigurationObject = cachedObject;
            this._initState = InitializationState.SUCCESS;
            return true;
        }

        this._initState = InitializationState.LOADING;
        this._initPromise = this.performAppConfigurationsInitialization();

        return this._initPromise;
    }

    private async performAppConfigurationsInitialization(): Promise<boolean> {
        const keys = Object.values(this._appConfigKeys);

        for (let attempt = 0; attempt <= this._retryConfig.maxRetries; attempt++) {
            try {
                if (attempt > 0) {
                    const delayMs = Math.min(
                        this._retryConfig.baseDelayMs * Math.pow(2, attempt - 1),
                        this._retryConfig.maxDelayMs
                    );
                    console.log(`AppConfig: Retry attempt ${attempt}/${this._retryConfig.maxRetries} after ${delayMs}ms`);
                    await this.delay(delayMs);
                }

                const response = await firstValueFrom(
                    this._http.post<ConfigApiResponse>(this._apiUrl, keys).pipe(
                        catchError((error) => {
                            console.error(`AppConfig: HTTP error on attempt ${attempt + 1}:`, error);
                            throw error;
                        })
                    )
                );

                if (response?.result) {
                    const success = this.parseAndConfigureResponse(response.result);
                    if (success) {
                        this._initState = InitializationState.SUCCESS;
                        this._initPromise = null;
                        return true;
                    }
                }

                console.warn(`AppConfig: Empty or invalid response on attempt ${attempt + 1}`);
            } catch (error) {
                console.error(`AppConfig: Failed attempt ${attempt + 1}/${this._retryConfig.maxRetries + 1}:`, error);

                if (attempt === this._retryConfig.maxRetries) {
                    console.error('AppConfig: All retry attempts exhausted. Initialization failed.');
                    this._initState = InitializationState.FAILED;
                    this._initPromise = null;
                    this.setFallbackAppConfigurations();
                    return false;
                }
            }
        }

        this._initState = InitializationState.FAILED;
        this._initPromise = null;
        this.setFallbackAppConfigurations();
        return false;
    }

    private parseAndConfigureResponse(result: unknown): boolean {
        try {
            let parsed: unknown = result;
            if (typeof parsed === 'string') {
                parsed = JSON.parse(parsed);
            }
            if (Array.isArray(parsed)) {
                this.configureAppConfigurations(parsed as KeyValueModel[]);
                return true;
            } else {
                console.error('AppConfig: Expected array but got', typeof parsed);
                return false;
            }
        } catch (parseError) {
            console.error('AppConfig: Error parsing configuration JSON:', parseError);
            return false;
        }
    }

    private delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    async forceRefreshConfigurations(): Promise<boolean> {
        this._initState = InitializationState.IDLE;
        this._initPromise = null;
        this.clearSessionStorage();
        return this.initializeApp();
    }

    private configureAppConfigurations(data: KeyValueModel[]): void {
        this._appConfigurations = data.reduce(
            (acc, item) => {
                acc[item.key] = item.value;
                return acc;
            },
            {} as Record<string, string>
        );

        this._appConfigurationObject = this.mapToConfigurationObject(data);
        this.saveToSessionStorage();
    }

    private mapToConfigurationObject(data: KeyValueModel[]): AppConfiguration {
        const configMap = data.reduce(
            (acc, item) => {
                acc[item.key] = item.value;
                return acc;
            },
            {} as Record<string, string>
        );

        return {
            agreements: this.parseAgreements(configMap[this._appConfigKeys.agreementSalesEnKey]),
            google: {
                web: { clientId: configMap[this._appConfigKeys.googleWebClientIdKey] || '' },
            },
            facebook: {
                appId: configMap[this._appConfigKeys.facebookAppIdKey] || '',
                web: { clientToken: configMap[this._appConfigKeys.facebookWebAppIdKey] || '' },
            },
            microsoft: {
                web: {
                    clientId: configMap[this._appConfigKeys.microsoftWebClientIdKey] || '',
                    tenantId: configMap[this._appConfigKeys.microsoftWebTenantIdKey] || '',
                },
            },
            apple: {
                web: { ssoIdentifierId: configMap[this._appConfigKeys.appleWebSSOIdentifierKey] || '' },
            },
            header: { xAppWeb: configMap[this._appConfigKeys.appHeaderXAppWebKey] || '' },
            localization: this.parseLocalization(configMap[this._appConfigKeys.localizationKey]),
            mixpanelProjectToken: configMap[this._appConfigKeys.mixpanelProjectTokenKey] || '',
            googleAnalytics: {
                measurementId: configMap[this._appConfigKeys.googleAnalyticsMeasurementIdKey] || '',
            },
        };
    }

    private parseAgreements(agreementsJson: string): AppConfiguration['agreements'] {
        try {
            if (!agreementsJson) {
                return {
                    tr: { accessKey: '', sales: '', terms: '' },
                    en: { accessKey: '', sales: '', terms: '' },
                };
            }
            return JSON.parse(agreementsJson);
        } catch (error) {
            console.error('Error parsing agreements JSON:', error);
            return {
                tr: { accessKey: '', sales: '', terms: '' },
                en: { accessKey: '', sales: '', terms: '' },
            };
        }
    }

    private parseLocalization(localizationJson: string): AppConfiguration['localization'] {
        try {
            if (!localizationJson) return [];
            const parsed = JSON.parse(localizationJson);
            if (Array.isArray(parsed)) {
                return parsed.filter(
                    (item: any) =>
                        item &&
                        typeof item.key === 'string' &&
                        typeof item.iso1 === 'string' &&
                        typeof item.url === 'string'
                );
            }
            return [];
        } catch (error) {
            console.error('Error parsing localization JSON:', error);
            return [];
        }
    }

    private setFallbackAppConfigurations(): void {
        this._appConfigurations = undefined;
        this._appConfigurationObject = undefined;
        this.clearSessionStorage();
    }

    private saveToSessionStorage(): void {
        if (this._appConfigurations) {
            this._sessionStorageService.setItem(this.SESSION_STORAGE_KEY, this._appConfigurations);
        }
        if (this._appConfigurationObject) {
            this._sessionStorageService.setItem(this.SESSION_STORAGE_OBJECT_KEY, this._appConfigurationObject);
        }
    }

    private loadFromSessionStorage(): Record<string, string> | null {
        return this._sessionStorageService.getItem<Record<string, string>>(this.SESSION_STORAGE_KEY);
    }

    private loadObjectFromSessionStorage(): AppConfiguration | null {
        return this._sessionStorageService.getItem<AppConfiguration>(this.SESSION_STORAGE_OBJECT_KEY);
    }

    private clearSessionStorage(): void {
        this._sessionStorageService.removeItem(this.SESSION_STORAGE_KEY);
        this._sessionStorageService.removeItem(this.SESSION_STORAGE_OBJECT_KEY);
    }

    clearConfigurations(): void {
        this._appConfigurations = undefined;
        this._appConfigurationObject = undefined;
        this._initState = InitializationState.IDLE;
        this._initPromise = null;
        this.clearSessionStorage();
        this.clearAuthenticatedConfigurations();
    }

    getConfigValue(key: string): string | undefined {
        return this._appConfigurations?.[key];
    }

    hasConfigKey(key: string): boolean {
        return this._appConfigurations?.[key] !== undefined;
    }

    get configuration(): AppConfiguration | undefined {
        return this._appConfigurationObject;
    }

    get agreements(): AppConfiguration['agreements'] | undefined {
        return this._appConfigurationObject?.agreements;
    }

    get headerConfig(): AppConfiguration['header'] | undefined {
        return this._appConfigurationObject?.header;
    }

    get mixpanelProjectToken(): string | undefined {
        return this._appConfigurationObject?.mixpanelProjectToken;
    }

    get googleAnalyticsConfig(): AppConfiguration['googleAnalytics'] {
        return this._appConfigurationObject?.googleAnalytics ?? { measurementId: '' };
    }

    get localizationConfig(): AppConfiguration['localization'] | undefined {
        return this._appConfigurationObject?.localization;
    }

    localizationUrl(language: string): string {
        const lr = this._appConfigurationObject?.localization?.find((l) => l.iso1 === language);
        return lr?.url || '';
    }

    localizationUrlByKey(key: string): string {
        const lr = this._appConfigurationObject?.localization?.find((l) => l.key === key);
        return lr?.url || '';
    }

    getAvailableLanguages(): string[] {
        return this._appConfigurationObject?.localization?.map((lr) => lr.iso1) || [];
    }

    getAvailableLanguageKeys(): string[] {
        return this._appConfigurationObject?.localization?.map((lr) => lr.key) || [];
    }

    isLanguageSupported(language: string): boolean {
        return this._appConfigurationObject?.localization?.some((lr) => lr.iso1 === language) || false;
    }

    isLanguageKeySupported(key: string): boolean {
        return this._appConfigurationObject?.localization?.some((lr) => lr.key === key) || false;
    }

    keyToIso1(key: string): string | null {
        return this._appConfigurationObject?.localization?.find((lr) => lr.key === key)?.iso1 || null;
    }

    iso1ToKey(iso1: string): string | null {
        return this._appConfigurationObject?.localization?.find((lr) => lr.iso1 === iso1)?.key || null;
    }

    getLanguageResource(iso1: string): LanguageResource | null {
        return this._appConfigurationObject?.localization?.find((lr) => lr.iso1 === iso1) || null;
    }

    getLanguageResourceByKey(key: string): LanguageResource | null {
        return this._appConfigurationObject?.localization?.find((lr) => lr.key === key) || null;
    }

    getAllLanguageResources(): LanguageResource[] {
        return this._appConfigurationObject?.localization || [];
    }

    get appConfigurations(): Record<string, string> | undefined {
        return this._appConfigurations;
    }

    // =================== AUTHENTICATED CONFIGURATION METHODS ===================

    async initializeAuthenticatedConfigurations(): Promise<boolean> {
        if (this._authInitState === InitializationState.SUCCESS) return true;

        if (this._authInitState === InitializationState.FAILED) {
            console.warn('AuthConfig: Previous initialization failed.');
            return false;
        }

        if (this._authInitState === InitializationState.LOADING && this._authInitPromise) {
            return this._authInitPromise;
        }

        const cachedConfig = this.loadAuthenticatedFromSessionStorage();
        if (cachedConfig) {
            this._authenticatedConfigurations = cachedConfig;
            this._authInitState = InitializationState.SUCCESS;
            initSentryFromDsn(this.sentryDsn);
            return true;
        }

        this._authInitState = InitializationState.LOADING;
        this._authInitPromise = this.performAuthenticatedConfigurationsInitialization();
        return this._authInitPromise;
    }

    private async performAuthenticatedConfigurationsInitialization(): Promise<boolean> {
        const keys = Object.values(this._authenticatedConfigKeys);

        for (let attempt = 0; attempt <= this._retryConfig.maxRetries; attempt++) {
            try {
                if (attempt > 0) {
                    const delayMs = Math.min(
                        this._retryConfig.baseDelayMs * Math.pow(2, attempt - 1),
                        this._retryConfig.maxDelayMs
                    );
                    console.log(`AuthConfig: Retry attempt ${attempt}/${this._retryConfig.maxRetries} after ${delayMs}ms`);
                    await this.delay(delayMs);
                }

                const response = await firstValueFrom(
                    this._http.post<ConfigApiResponse>(this._apiUrl, keys).pipe(
                        catchError((error) => {
                            console.error(`AuthConfig: HTTP error on attempt ${attempt + 1}:`, error);
                            throw error;
                        })
                    )
                );

                if (response?.result) {
                    const success = this.parseAndConfigureAuthenticatedResponse(response.result);
                    if (success) {
                        this._authInitState = InitializationState.SUCCESS;
                        this._authInitPromise = null;
                        return true;
                    }
                }

                console.warn(`AuthConfig: Empty or invalid response on attempt ${attempt + 1}`);
            } catch (error) {
                console.error(`AuthConfig: Failed attempt ${attempt + 1}/${this._retryConfig.maxRetries + 1}:`, error);

                if (attempt === this._retryConfig.maxRetries) {
                    console.error('AuthConfig: All retry attempts exhausted. Initialization failed.');
                    this._authInitState = InitializationState.FAILED;
                    this._authInitPromise = null;
                    this.setFallbackAuthenticatedConfigurations();
                    return false;
                }
            }
        }

        this._authInitState = InitializationState.FAILED;
        this._authInitPromise = null;
        this.setFallbackAuthenticatedConfigurations();
        return false;
    }

    private parseAndConfigureAuthenticatedResponse(result: unknown): boolean {
        try {
            let parsed: unknown = result;
            if (typeof parsed === 'string') {
                parsed = JSON.parse(parsed);
            }
            if (Array.isArray(parsed)) {
                this.configureAuthenticatedConfigurations(parsed as KeyValueModel[]);
                return true;
            } else {
                console.error('AuthConfig: Expected array but got', typeof parsed);
                return false;
            }
        } catch (parseError) {
            console.error('AuthConfig: Error parsing configuration JSON:', parseError);
            return false;
        }
    }

    private configureAuthenticatedConfigurations(data: KeyValueModel[]): void {
        this._authenticatedConfigurations = data.reduce(
            (acc, item) => {
                acc[item.key] = item.value;
                return acc;
            },
            {} as Record<string, string>
        );
        this.saveAuthenticatedToSessionStorage();
        initSentryFromDsn(this.sentryDsn);
    }

    private setFallbackAuthenticatedConfigurations(): void {
        this._authenticatedConfigurations = undefined;
        this.clearAuthenticatedSessionStorage();
    }

    private saveAuthenticatedToSessionStorage(): void {
        if (this._authenticatedConfigurations) {
            this._sessionStorageService.setItem(
                this.AUTHENTICATED_SESSION_STORAGE_KEY,
                this._authenticatedConfigurations
            );
        }
    }

    private loadAuthenticatedFromSessionStorage(): Record<string, string> | null {
        return this._sessionStorageService.getItem<Record<string, string>>(this.AUTHENTICATED_SESSION_STORAGE_KEY);
    }

    private clearAuthenticatedSessionStorage(): void {
        this._sessionStorageService.removeItem(this.AUTHENTICATED_SESSION_STORAGE_KEY);
    }

    getAuthenticatedConfigValue(key: string): string | undefined {
        return this._authenticatedConfigurations?.[key];
    }

    hasAuthenticatedConfigKey(key: string): boolean {
        return this._authenticatedConfigurations?.[key] !== undefined;
    }

    get mapboxApiToken(): string | undefined {
        return this.getAuthenticatedConfigValue(this._authenticatedConfigKeys.mapboxApiTokenClientSecretKey);
    }

    get mapboxStyle(): string | undefined {
        return this.getAuthenticatedConfigValue(this._authenticatedConfigKeys.mapboxStyleKey);
    }

    /** Sentry DSN: authenticated `App.Web.Sentry.DSN` when present, else `environment.sentryDsn`. */
    get sentryDsn(): string {
        const fromAuth = (
            this.getAuthenticatedConfigValue(this._authenticatedConfigKeys.sentryDsnWebKey) || ''
        ).trim();
        if (fromAuth) {
            return fromAuth;
        }
        return (environment.sentryDsn || '').trim();
    }

    clearAuthenticatedConfigurations(): void {
        this._authenticatedConfigurations = undefined;
        this._authInitState = InitializationState.IDLE;
        this._authInitPromise = null;
        this.clearAuthenticatedSessionStorage();
    }

    get authenticatedConfigurations(): Record<string, string> | undefined {
        return this._authenticatedConfigurations;
    }

    async forceRefreshAuthenticatedConfigurations(): Promise<boolean> {
        this._authInitState = InitializationState.IDLE;
        this._authInitPromise = null;
        this.clearAuthenticatedSessionStorage();
        return this.initializeAuthenticatedConfigurations();
    }

    get hasAuthenticatedConfigurations(): boolean {
        return this._authInitState === InitializationState.SUCCESS && !!this._authenticatedConfigurations;
    }

    get microsoftConfig(): AppConfiguration['microsoft'] | undefined {
        return this._appConfigurationObject?.microsoft;
    }

    get appleConfig(): AppConfiguration['apple'] | undefined {
        return this._appConfigurationObject?.apple;
    }

    get googleConfig(): AppConfiguration['google'] | undefined {
        return this._appConfigurationObject?.google;
    }

    get facebookConfig(): AppConfiguration['facebook'] | undefined {
        return this._appConfigurationObject?.facebook;
    }
}
